import * as React from "react";
import BottomDrawer from ".";
import AppBar from "../appbar";
import PageCount from "../count/PageCount";
import Button from "../button";
import CompetitionTermsScreen from "../../layout/competition/CompetitionTermsScreen";
import CompetitionInfoScreen from "../../layout/competition/CompetitionInfoScreen";
import CompetitionDateScreen from "../../layout/competition/CompetitionDateScreen";
import CompetitionDateGroupScreen from "../../layout/competition/CompetitionDateGroupScreen";
import CompetitionScheduleScreen from "../../layout/competition/CompetitionScheduleScreen";
import CompetitionPaymentConfirm from "../../layout/competition/CompetitionPaymentConfirm";
import { useModalStore } from "../../../lib/store/useModalStore";
import { useBlocker, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TournamentApplyInput } from "../../../types/tournament.types";
import { tournamentApplySchema } from "../../../lib/schemas/tournament.schema";
import type {
  CalendarItem,
  TournamentDetailResponse,
} from "../../../@apis/dto/response/torunament.response";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getQueryClient } from "../../../@apis/queryClient";
import {
  TournamentApply,
  TournamentApplyUpdate,
} from "../../../@apis/v1/tournament.service";
import { tournamentPayingApply } from "../../../@apis/v1/payment.service";
import { useTournamentTerms } from "../../../@apis/hooks/useTournament";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";
import { staticURL } from "../../../lib/utils/imageUtils";
import {
  isValidEmail,
  unavailableScheduleMessage,
} from "../../../lib/utils/tournamentUtils";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import { useDeviceStore } from "../../../lib/store/useDeviceStore";
import { useNicePayLauncher } from "../../../hooks/useNicePayLauncher";

interface CompetitionDrawerProps {
  id: string | number;
  open: boolean;
  onClose: () => void;
  item: TournamentDetailResponse;
  type?: "add" | "edit";
  calendarList: CalendarItem[];
  // 수정 모드에서도 전체 화면(풀 팝업)으로 렌더할지 여부
  fullPopup?: boolean;
}

async function urlToFile(url: string): Promise<File> {
  const response = await fetch(url);
  const blob = await response.blob();
  const afterLastSlash = url.split("/").pop() || "";
  const parts = afterLastSlash.split("_", 2);
  return new File([blob], parts[1] || afterLastSlash, { type: blob.type });
}

// 대기열 미입장 거절 — 입장 유효시간이 지나 제출한 경우라 재진입을 안내한다
const queueGateContent = (code?: string | null) =>
  code === "P135"
    ? "대기열 입장 시간이 지나 접수할 수 없습니다.\n접수 화면에 다시 들어와 주세요."
    : null;

const applyClosedContent = (code?: string | null) => {
  if (code === "P009") return "정원이 마감되었습니다.";
  if (code === "P163")
    return "선택하신 일정의 해당 성별 신청 정원이 마감되었습니다.";
  return null;
};

export default function CompetitionDrawer({
  id,
  open,
  onClose,
  item,
  type = "add",
  calendarList,
  fullPopup = false,
}: CompetitionDrawerProps) {
  const { openModal } = useModalStore();
  const { myInfo } = useMyInfoStore();
  const { deviceType } = useDeviceStore();
  const navigate = useNavigate();
  const { launch: launchNicePay, formElement: nicePayForm } =
    useNicePayLauncher();

  const isCertificateAccept =
    item?.tournamentInfo.isCertificateAccept !== false;
  const isFullPopup = type === "add" || fullPopup;
  // 카드/가상계좌(online)는 접수 전 결제 확인 단계를 거친다.
  const isOnlinePay = item?.tournamentInfo.paymentMethod === "online";
  const entryFee = item?.tournamentInfo.entryFee ?? 0;
  const totalAmount = entryFee;
  const certificateAcceptMessage =
    item?.tournamentInfo.certificateAcceptMessage;
  const isMultiDepth = item?.tournamentInfo.calendarDepth === 2;
  const skipDateStep = type === "edit" && calendarList.length === 0;
  const isCollectRegion = item?.tournamentInfo.isCollectRegion === true;
  const isReapply = item?.tournamentInfo.isReapply === true && type === "add";

  const initialSelectedDate = (() => {
    // 재접수 대회는 날짜를 항상 직접 선택하도록 초기값을 비운다.
    if (isReapply) return "";
    const d = item?.tournamentInfo.calendarInfo?.date;
    return typeof d === "string" ? d : "";
  })();
  const [selectedDate, setSelectedDate] = React.useState(initialSelectedDate);
  const [scheduleEmpty, setScheduleEmpty] = React.useState(false);
  const [selectedCalendar, setSelectedCalendar] =
    React.useState<CalendarItem | null>(null);

  const [currentStep, setCurrentStep] = React.useState(0);
  // 온라인 결제(카드/가상계좌) 접수 시 마지막 스텝(접수하기) 이후 결제 확인 화면 노출 여부.
  // 결제 확인은 스텝에 포함하지 않고 오버레이 화면으로 띄운다.
  const [showPaymentConfirm, setShowPaymentConfirm] = React.useState(false);
  const next = React.useCallback(() => setCurrentStep((s) => s + 1), []);
  const prev = React.useCallback(
    () => setCurrentStep((s) => (s > 0 ? s - 1 : s)),
    [],
  );

  // 접수 종료/제출 등 의도된 이탈 시 뒤로가기 가로채기를 우회하기 위한 플래그
  const bypassBackGuardRef = React.useRef(false);

  // 풀 화면 접수 플로우에서 하드웨어/브라우저 뒤로가기는 라우트를 벗어나지 않고
  // 이전 스텝으로 이동한다. 첫 스텝(0)에서는 그대로 이전 화면(대회 상세)으로 나간다.
  const blocker = useBlocker(
    ({ historyAction }) =>
      type === "add" &&
      open &&
      historyAction === "POP" &&
      (currentStep > 0 || showPaymentConfirm) &&
      !bypassBackGuardRef.current,
  );

  React.useEffect(() => {
    if (blocker.state === "blocked") {
      // 결제 확인 화면에서는 이전 스텝(접수 정보)으로 돌아간다.
      if (showPaymentConfirm) setShowPaymentConfirm(false);
      else prev();
      blocker.reset();
    }
  }, [blocker, prev, showPaymentConfirm]);

  const [file, setFile] = React.useState<File[]>([]);
  // 얼럿 확인 시 이동/포커스할 정보 입력 영역
  const [focusTarget, setFocusTarget] = React.useState<string | null>(null);

  React.useEffect(() => {
    // 재접수 대회는 첫 접수처럼 첨부파일도 비운 상태로 시작한다.
    if (type !== "edit" || isReapply) return;

    const fileList = item?.tournamentInfo?.apply?.fileList || [];
    if (fileList.length === 0) return;

    Promise.all(
      fileList.map(({ fileUrl }) => urlToFile(staticURL(fileUrl))),
    ).then((files) => {
      setFile(files);
    });
  }, [type, item]);

  const methos = useForm<TournamentApplyInput>({
    resolver: zodResolver(tournamentApplySchema),
    // 재접수 대회는 첫 접수처럼 모든 값을 초기 상태로 두고 이전 신청 내역을 물지 않는다.
    defaultValues: isReapply
      ? {
          email: "",
          calendarId: undefined,
          // 자격증 보유 여부 기본값은 미보유
          haveProCertificate: false,
          region: undefined,
        }
      : {
          email: item?.tournamentInfo.apply?.email || "",
          calendarId: item?.tournamentInfo.calendarInfo?.id,
          haveProCertificate:
            item?.tournamentInfo.apply?.haveProCertificate ?? false,
          region: item?.tournamentInfo.apply?.region,
        },
  });
  const { watch, getValues } = methos;
  const values = watch();

  // 필수 약관 동의 여부 확인용 (약관 화면과 같은 쿼리 캐시를 사용)
  const { data: termsData } = useQuery({
    ...useTournamentTerms(id),
    enabled: !!id && type === "add",
  });
  const requiredTermsAgreed = (termsData?.data?.termsList ?? [])
    .filter((term) => term.isRequired)
    .every((term) =>
      (values.termAgreeList ?? []).some(
        (agree) => agree?.termsId === term.id && agree.isAgreed,
      ),
    );

  const steps = React.useMemo<
    {
      id: string;
      component: React.ComponentType<any>;
      props: Record<string, any>;
      validate: () => boolean;
    }[]
  >(
    () => [
      ...(type === "add"
        ? [
            {
              id: "terms",
              component: CompetitionTermsScreen,
              props: { id, type },
              // 미동의 시에도 버튼은 활성화하고, onClick에서 얼럿으로 안내한다.
              validate: (): boolean => true,
            },
          ]
        : []),
      ...(skipDateStep
        ? []
        : isMultiDepth
          ? [
              {
                id: "dateGroup",
                component: CompetitionDateGroupScreen,
                props: {
                  calendarList,
                  selectedDate,
                  onSelectDate: (date: string) => {
                    if (date !== selectedDate) {
                      methos.setValue(
                        "calendarId",
                        undefined as unknown as number,
                      );
                      setSelectedCalendar(null);
                    }
                    setSelectedDate(date);
                  },
                },
                // 미선택 시에도 버튼은 활성화하고, onClick에서 얼럿으로 안내한다.
                validate: (): boolean => true,
              },
              {
                id: "schedule",
                component: CompetitionScheduleScreen,
                props: {
                  tournamentId: id,
                  selectedDate,
                  onSelectCalendar: setSelectedCalendar,
                  onEmptyChange: setScheduleEmpty,
                },
                // 미선택 시에도 버튼은 활성화하고, onClick에서 얼럿으로 안내한다.
                validate: (): boolean => true,
              },
            ]
          : [
              {
                id: "date",
                component: CompetitionDateScreen,
                props: { calendarList },
                // 날짜 미선택 시에도 버튼은 활성화하고, onClick에서 얼럿으로 안내한다.
                validate: (): boolean => true,
              },
            ]),
      {
        id: "info",
        component: CompetitionInfoScreen,
        props: {
          tournamentName: item?.tournamentInfo.name,
          isCollectRegion,
          isCertificateAccept,
          certificateMessage: certificateAcceptMessage,
          files: file,
          setFiles: setFile,
          focusTarget,
          onFocusHandled: () => setFocusTarget(null),
        },
        // 미입력 시에도 버튼은 활성화하고, onClick에서 얼럿으로 안내한다.
        validate: (): boolean => true,
      },
    ],
    [
      id,
      type,
      item,
      calendarList,
      values,
      file,
      isCertificateAccept,
      certificateAcceptMessage,
      isMultiDepth,
      skipDateStep,
      isCollectRegion,
      selectedDate,
      methos,
      selectedCalendar,
    ],
  );

  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;
  const currentStepConfig = steps[currentStep];
  const canProceed = currentStepConfig?.validate() ?? false;

  const applyMutation = useMutation({
    mutationFn: (variables: TournamentApplyInput & { file?: File[] }) => {
      const { file = [], ...form } = variables;
      return TournamentApply(id, form, file);
    },
    onSuccess: (res) => {
      // 무통장입금 접수 완료: 온라인 결제와 동일하게 신청 완료 페이지로 이동
      // (온라인 결제는 payingApplyMutation → NICEPAY 로 별도 처리)
      // onClose 는 접수 화면을 뒤로 닫는 동작이라 이동과 충돌하므로 호출하지 않고,
      // 갱신이 필요한 대회 상세만 무효화한다.
      bypassBackGuardRef.current = true;
      getQueryClient().invalidateQueries({ queryKey: ["tournament-detail", id] });
      navigate(`/tournament-apply/${res.data}/complete`, { replace: true });
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "접수 실패",
        content:
          queueGateContent(error?.code) ??
          applyClosedContent(error?.code) ??
          error?.message,
      });
    },
  });

  // 온라인 결제(카드/가상계좌): 접수 + 결제 요청 후 NICEPAY 결제창 실행
  const payingApplyMutation = useMutation({
    mutationFn: (variables: TournamentApplyInput & { file?: File[] }) => {
      const { file = [], ...form } = variables;
      return tournamentPayingApply(id, form, file);
    },
    onSuccess: (res) => {
      const { success, data, error } = res;
      if (success) {
        launchNicePay(data.nicePayRequest);
      } else {
        setPaymentLaunched(false);
        openModal("ALERT_DIALOG", {
          title: "에러",
          content: applyClosedContent(error?.code) ?? error?.message,
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      setPaymentLaunched(false);
      openModal("ALERT_DIALOG", {
        title: "에러",
        content:
          queueGateContent(error?.code) ??
          applyClosedContent(error?.code) ??
          (error?.code === "C003" ? "요청에 실패하였습니다." : error?.message),
      });
    },
  });

  const editErrorContent = (code?: string | null, fallback?: string | null) => {
    const closed = applyClosedContent(code);
    if (closed) return closed;
    switch (code) {
      case "INVALID_TOURNAMENT_JOIN_OR_UPDATE_TIME":
        return "접수 기간이 아니어서 수정할 수 없습니다.";
      case "APPLIED_COUNT_EXCEEDED":
        return "신청/승인 정원이 초과되어 변경할 수 없습니다.";
      case "ALREADY_APPLIED_ON_OTHER_TOURNAMENT":
        return "같은 날짜에 이미 신청한 다른 대회가 있습니다.";
      case "APPLY_STATUS_FAILED":
        return "미입금으로 마감 처리되어 수정할 수 없습니다.";
      case "C003":
        return "요청에 실패하였습니다.";
      default:
        return fallback ?? "요청에 실패하였습니다.";
    }
  };

  const editApplyMutation = useMutation({
    mutationFn: (variables: TournamentApplyInput & { file?: File[] }) => {
      const { file = [], ...form } = variables;
      return TournamentApplyUpdate(id, form, file);
    },
    onSuccess: (res) => {
      const { success, error } = res;
      if (success) {
        onClose();
        openModal("ALERT_DIALOG", {
          title: "수정 완료",
          content: "수정을 완료하였습니다.",
        });
      } else {
        openModal("ALERT_DIALOG", {
          title: "에러",
          content: editErrorContent(error?.code, error?.message),
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "에러",
        content: editErrorContent(error?.code, error?.message),
      });
    },
  });

  // 스텝 이동 시 스크롤을 맨 위로 초기화한다.
  React.useEffect(() => {
    document.querySelector("main")?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [currentStep]);

  // 결제 요청 응답과 결제창 노출 사이에 버튼이 다시 눌려 중복 접수되는 것을 막는다.
  // 결제창은 화면을 벗어나며 뜨므로, 사용자가 이 화면으로 돌아왔을 때 잠금을 푼다.
  // 상태는 렌더(버튼 비활성)용이고, 연타는 상태 반영 전에 들어오므로 ref로 즉시 막는다.
  const paymentLaunchedRef = React.useRef(false);
  const [isPaymentLaunched, setIsPaymentLaunched] = React.useState(false);

  const setPaymentLaunched = React.useCallback((launched: boolean) => {
    paymentLaunchedRef.current = launched;
    setIsPaymentLaunched(launched);
  }, []);

  React.useEffect(() => {
    if (!isPaymentLaunched) return;
    const unlockOnReturn = () => {
      if (document.visibilityState === "visible") setPaymentLaunched(false);
    };
    document.addEventListener("visibilitychange", unlockOnReturn);
    window.addEventListener("pageshow", unlockOnReturn);
    return () => {
      document.removeEventListener("visibilitychange", unlockOnReturn);
      window.removeEventListener("pageshow", unlockOnReturn);
    };
  }, [isPaymentLaunched, setPaymentLaunched]);

  const isSubmitting =
    applyMutation.isPending ||
    editApplyMutation.isPending ||
    payingApplyMutation.isPending ||
    isPaymentLaunched;

  // 온라인 결제(카드/가상계좌) 결제하기: 접수+결제 요청 후 NICEPAY 실행 (모바일앱 전용)
  const handlePayment = () => {
    if (isSubmitting || paymentLaunchedRef.current) return;

    if (deviceType === "web") {
      openModal("ALERT_DIALOG", {
        title: "결제 불가",
        content: "웹에서는 결제할 수 없습니다.\n보이스파크 앱에서 결제하세요.",
      });
      return;
    }

    const allValues = getValues();
    const submitFile = isCertificateAccept ? file : [];
    const submitValues = isCertificateAccept
      ? allValues
      : { ...allValues, haveProCertificate: false };
    setPaymentLaunched(true);
    payingApplyMutation.mutate({ file: submitFile, ...submitValues });
  };

  const handleNext = () => {
    if (isSubmitting) return;

    if (currentStepConfig.id === "terms" && !requiredTermsAgreed) {
      openModal("ALERT_DIALOG", {
        title: "알림",
        content: "필수 약관을 모두 선택해 주세요.",
      });
      return;
    }

    if (
      (currentStepConfig.id === "dateGroup" && calendarList.length === 0) ||
      (currentStepConfig.id === "date" && calendarList.length === 0) ||
      (currentStepConfig.id === "schedule" && scheduleEmpty)
    ) {
      openModal("ALERT_DIALOG", {
        title: "접수할 수 없습니다",
        content: unavailableScheduleMessage(myInfo.gender),
      });
      return;
    }

    if (currentStepConfig.id === "dateGroup" && !selectedDate) {
      openModal("ALERT_DIALOG", {
        title: "날짜를 선택해 주세요.",
      });
      return;
    }

    if (currentStepConfig.id === "date" && !getValues("calendarId")) {
      openModal("ALERT_DIALOG", {
        title: "날짜를 선택해 주세요.",
      });
      return;
    }

    if (currentStepConfig.id === "schedule" && !getValues("calendarId")) {
      openModal("ALERT_DIALOG", {
        title: "일정을 선택해주세요.",
      });
      return;
    }

    // 화면 상단부터 순서대로 첫 번째 미입력 항목을 안내하고, 확인 시 해당 영역으로 이동한다.
    if (currentStepConfig.id === "info") {
      if (isCollectRegion && !getValues("region")) {
        openModal("ALERT_DIALOG", {
          title: "지역을 선택해 주세요.",
          onConfirm: () => setFocusTarget("region"),
        });
        return;
      }

      if (
        isCertificateAccept &&
        getValues("haveProCertificate") === true &&
        file.length === 0
      ) {
        openModal("ALERT_DIALOG", {
          title: "자격증 파일을 첨부해 주세요.",
          onConfirm: () => setFocusTarget("certificate"),
        });
        return;
      }

      const email = getValues("email");
      if (email && !isValidEmail(email)) {
        openModal("ALERT_DIALOG", {
          title: "올바른 이메일 형식을\n입력해 주세요.",
          onConfirm: () => setFocusTarget("email"),
        });
        return;
      }
    }

    if (isLastStep) {
      // 온라인 결제(카드/가상계좌) 접수: 결제 확인 화면으로 이동(스텝 아님)
      if (type === "add" && isOnlinePay) {
        setShowPaymentConfirm(true);
        return;
      }

      const allValues = getValues();
      const submitFile = isCertificateAccept ? file : [];
      const submitValues = isCertificateAccept
        ? allValues
        : { ...allValues, haveProCertificate: false };
      return type === "add"
        ? applyMutation.mutate({
            file: submitFile,
            ...submitValues,
          })
        : editApplyMutation.mutate({
            file: submitFile,
            ...submitValues,
          });
    }

    next();
  };

  const stepContent = (
    <FormProvider {...methos}>
      {currentStepConfig &&
        React.createElement(
          currentStepConfig.component,
          currentStepConfig.props,
        )}
    </FormProvider>
  );

  const footerButtons = (
    <>
      {currentStep !== 0 && (
        <Button
          className="w-full text-sm"
          variant={"white"}
          onClick={prev}
          label="이전"
        />
      )}
      <Button
        className="w-full text-sm"
        disabled={!canProceed || isSubmitting}
        onClick={handleNext}
        variant={!canProceed || isSubmitting ? "disabled" : "black"}
        label={
          isLastStep ? (type === "edit" ? "수정완료" : "접수하기") : "다음"
        }
      />
    </>
  );

  // 헤더 뒤로가기: 접수 종료 확인 후 이전 화면(대회 상세)으로 이동
  const handleHeaderBack = () =>
    openModal("CONFIRM_DIALOG", {
      title:
        type === "edit" ? "접수 수정을 종료할까요?" : "대회 접수를 종료할까요?",
      content: "입력한 내용은 저장되지 않습니다.",
      onClick: () => {
        // 스텝 뒤로가기 가로채기를 우회하고 라우트를 벗어난다.
        bypassBackGuardRef.current = true;
        onClose();
      },
    });

  // 접수하기는 signup 화면과 동일하게 페이지 전체를 차지하는 풀 화면으로 렌더한다.
  if (isFullPopup) {
    if (!open) return null;

    // 카드/가상계좌 결제: 접수하기 이후 결제 확인 화면(스텝 아님)
    if (showPaymentConfirm) {
      return (
        <div className="flex flex-col min-h-[100dvh]">
          <AppBar
            title="결제 확인"
            backButton
            onBack={() =>
              openModal("CONFIRM_DIALOG", {
                title: "대회 접수를 취소할까요?",
                content: "입력한 내용은 저장되지 않습니다.",
                onClick: () => {
                  // 접수 플로우를 벗어나 이전 화면(대회 상세 등)으로 이동
                  bypassBackGuardRef.current = true;
                  onClose();
                },
              })
            }
          />

          <div className="flex-1 bg-[#f2f2f7] p-[20px]">
            <FormProvider {...methos}>
              <CompetitionPaymentConfirm
                tournamentName={item?.tournamentInfo.name}
                calendarList={
                  selectedCalendar ? [selectedCalendar] : calendarList
                }
                hasClass={item?.tournamentInfo.hasClass ?? false}
                entryFee={entryFee}
                isCertificateAccept={isCertificateAccept}
              />
            </FormProvider>
          </div>

          <div className="sticky bottom-0 left-0 z-50 p-[12px] flex gap-[10px] bg-white pb-[calc(12px+var(--safe-area-inset-bottom))]">
            <Button
              className="flex-1 font-normal"
              variant={"white"}
              label="이전"
              onClick={() => setShowPaymentConfirm(false)}
            />
            <Button
              className="flex-[2] font-normal"
              disabled={isSubmitting}
              onClick={handlePayment}
              variant={isSubmitting ? "disabled" : "primary"}
              label={`${totalAmount.toLocaleString()}원 결제하기`}
            />
          </div>
          {nicePayForm}
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        <AppBar title="대회 접수" backButton onBack={handleHeaderBack} />

        <section className="px-[12px] flex flex-col justify-between min-h-[calc(100vh-56px)] space-y-2">
          <div className="p-[8px]">
            <PageCount
              className="mb-[20px]"
              totalCount={totalSteps}
              currentPage={currentStep + 1}
            />
            <div className="flex-1">{stepContent}</div>
          </div>

          <div className="flex-row w-full gap-[10px] sticky bottom-0 left-0 z-50 bg-white py-[12px]">
            {footerButtons}
          </div>
        </section>
      </div>
    );
  }

  return (
    <BottomDrawer open={open} onClose={onClose}>
      <div>
        <div className="flex-row-between flex-wrap mb-[24px]">
          <p className="text-base font-bold whitespace-nowrap">
            대회 접수 {type == "edit" && "수정"}
          </p>
          <PageCount totalCount={totalSteps} currentPage={currentStep + 1} />
        </div>

        <section className="flex flex-col justify-between">
          <div className="flex-1">{stepContent}</div>

          <div className="py-[15px] flex-row w-full gap-[10px]">
            {footerButtons}
          </div>
        </section>
      </div>
    </BottomDrawer>
  );
}
